import config from "@/config";

/**
 * Gromor KYC — DigiLocker in a new tab, then poll when we get focus back.
 *
 * ── WHY A NEW TAB, NOT A REDIRECT ─────────────────────────────────────────────────────────
 * Gromor cannot yet give us a custom success/failure redirect URL, so a tenant who navigates
 * away lands on THEIR page and has no way back into our journey. Opening DigiLocker in a new
 * tab keeps our tab mounted: no reload, no lost state, and the tenant closing that tab is our
 * signal to poll. This was Gromor's own suggestion.
 *
 * ── WHY A LINK THE TENANT TAPS, NOT A TAB OPENED IN ADVANCE ───────────────────────────────
 * The page is prepared first (the card and the mandate screen show the wait), then offered as a
 * real `<a target="_blank">`. We used to open a blank tab inside the tap and fill it once the
 * vendor answered — on production (28 Sep) tenants sat on a white tab for up to ~20 s and
 * thought it had broken. A tap on a link is never blocked as a popup.
 *
 * ── WHY POLL ON FOCUS, NOT ON A TIMER ─────────────────────────────────────────────────────
 * There is NO Gromor KYC webhook — their `callbackUrl` is null and none of the callback types
 * we receive is KYC. Polling is the designed mechanism, not a workaround. Because our tab
 * stays alive, the honest trigger is it regaining focus rather than a fixed interval.
 *
 * A tenant who never comes back at all is recovered server-side by the hourly reconcile cron,
 * so nothing here needs to handle "closed the laptop".
 */

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem(config.LOCAL_KEYS.SIGNUP_TOKEN)}`,
});

/**
 * Ask cpcore for the tenant's DigiLocker link.
 *
 * Idempotent server-side: a stored link is reused rather than regenerated, because every
 * regenerate mints a new OAuth state and would strand a tenant who is mid-flow on the old one.
 * Once KYC is complete there is no link at all — `complete: true` means "refresh, you are done".
 *
 * @returns {Promise<{ link: string | null, complete: boolean }>}
 */
export async function startGromorKyc() {
  const response = await fetch(`${config.API_URL}/gromor-kyc/start`, {
    method: "POST",
    headers: authHeaders(),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.message || "Could not start identity verification.");
  }
  return {
    link: body?.data?.link || null,
    complete: Boolean(body?.data?.complete),
  };
}

/**
 * Start the selfie. Returns a Digio session URL, or { done: true } if it is already captured.
 *
 * Runs BEFORE DigiLocker: both are required, and doing the quick one first means the slow
 * government flow is the last thing the tenant does, rather than something they come back from
 * only to be asked for more.
 */
export async function startGromorSelfie() {
  const response = await fetch(`${config.API_URL}/gromor-kyc/selfie`, {
    method: "POST",
    headers: authHeaders(),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.message || "Could not start selfie capture.");
  }
  return {
    sessionUrl: body?.data?.sessionUrl || null,
    done: Boolean(body?.data?.done),
  };
}

/** One poll. Safe to repeat — it never touches the link endpoint. */
export async function fetchGromorKycStatus() {
  const response = await fetch(`${config.API_URL}/gromor-kyc/status`, {
    method: "GET",
    headers: authHeaders(),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.message || "Could not check verification status.");
  }
  return {
    complete: Boolean(body?.data?.complete),
    status: body?.data?.status ?? null,
  };
}

/**
 * Poll a few times after the tenant returns.
 *
 * DigiLocker usually settles within seconds of them finishing, but the record can lag the
 * redirect slightly — so one poll is occasionally too eager. A handful of short retries covers
 * that without becoming a background timer; if none of them sees it, the tenant simply taps
 * again or the cron picks them up.
 */
export async function pollGromorKycUntilComplete({
  attempts = 4,
  intervalMs = 1500,
} = {}) {
  let last = { complete: false, status: null };

  for (let i = 0; i < attempts; i += 1) {
    try {
      last = await fetchGromorKycStatus();
      if (last.complete) return last;
    } catch (error) {
      // A failed poll is not a failed KYC — keep trying, and let the caller decide.
      console.error("Gromor KYC poll failed", error);
    }
    if (i < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  return last;
}
